import { App } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { FileDownloadStatusEnum } from '../../enums/FileDownloadStatusEnum';
import type { GetFilesPayload } from '../../interfaces/FileApiService';
import type File from '../../interfaces/File';
import type ProcessStatus from '../../interfaces/ProcessStatus';
import {
  isDownloadCancelled,
  registerDownloadAbort,
  triggerBlobDownload,
  unregisterDownloadAbort,
} from '../../lib/fileDownload';
import { FileService } from '../../services/fileApiService';
import { useAppStore } from '../../store/store';
import {
  buildExportFolderFileListCsv,
  buildExportFolderFileListFilename,
  buildExportFolderFileListJson,
  buildExportFolderFileListSpreadsheetMl,
  createExportFolderFileListDateFormatter,
  CSV_UTF8_BOM,
  EXPORT_FOLDER_FILE_LIST_TAGS_JOIN,
  type ExportFolderFileListColumn,
  type ExportFolderFileListDateFormat,
  type ExportFolderFileListDelimiter,
  type ExportFolderFileListFormat,
  type ExportFolderFileListTagJoin,
} from '../../utils/exportFolderFileListCsv/exportFolderFileListCsv';
import { generateUUID } from '../../utils/utils';

const fileService = new FileService();

/** Marker in ProcessStatus.additionalData so UI can detect an in-flight folder file-list export. */
export const FOLDER_FILE_LIST_EXPORT_MARKER = 'folder-file-list-export';

const PAGE_SIZE = 100;

const BLOB_MIME_BY_FORMAT: Record<ExportFolderFileListFormat, string> = {
  csv: 'text/csv;charset=utf-8;',
  json: 'application/json;charset=utf-8;',
  excelXml: 'application/vnd.ms-excel;charset=utf-8;',
};

export type StartExportFolderFileListArgs = {
  filters: GetFilesPayload;
  folderName?: string | null;
  format?: ExportFolderFileListFormat;
  columns: ExportFolderFileListColumn[];
  delimiter?: ExportFolderFileListDelimiter;
  tagsJoin?: ExportFolderFileListTagJoin;
  dateFormat?: ExportFolderFileListDateFormat;
  tagTitleById?: (tagId: number) => string | undefined;
};

export function isFolderFileListExportProcess(process: ProcessStatus): boolean {
  return process.additionalData === FOLDER_FILE_LIST_EXPORT_MARKER;
}

function snapshotListFilters(filters: GetFilesPayload): GetFilesPayload {
  const snapshot: GetFilesPayload = {
    ...filters,
    offset: 0,
    count: PAGE_SIZE,
  };
  delete snapshot.refreshToken;
  if (snapshot.onlyExpired !== true) {
    delete snapshot.onlyExpired;
  }
  return snapshot;
}

export const useExportFolderFileList = () => {
  const { t } = useTranslation();
  const { message, notification } = App.useApp();
  const locale = useAppStore((state) => state.locale);

  const setFileDownloadProcessStatuses = useAppStore(
    (state) => state.setFileDownloadProcessStatuses,
  );
  const fileDownloadProcessStatuses = useAppStore((state) => state.fileDownloadProcessStatuses);

  const isExportRunning = fileDownloadProcessStatuses.some(
    (process) =>
      isFolderFileListExportProcess(process) &&
      process.status === FileDownloadStatusEnum.Processing,
  );

  const startExport = useCallback(
    async ({
      filters,
      folderName,
      format = 'csv',
      columns,
      delimiter = ',',
      tagsJoin = EXPORT_FOLDER_FILE_LIST_TAGS_JOIN,
      dateFormat = 'localeShort',
      tagTitleById,
    }: StartExportFolderFileListArgs): Promise<void> => {
      if (columns.length === 0) {
        return;
      }

      const alreadyRunning = useAppStore
        .getState()
        .fileDownloadProcessStatuses.some(
          (process) =>
            isFolderFileListExportProcess(process) &&
            process.status === FileDownloadStatusEnum.Processing,
        );
      if (alreadyRunning) {
        return;
      }

      const processId = generateUUID();
      const filename = buildExportFolderFileListFilename(folderName, format);
      const baseFilters = snapshotListFilters(filters);

      setFileDownloadProcessStatuses((previousStatuses) => [
        ...previousStatuses,
        {
          additionalData: FOLDER_FILE_LIST_EXPORT_MARKER,
          processId,
          progressPercentage: 0,
          status: FileDownloadStatusEnum.Processing,
          name: filename,
          isDirectFileDownload: true,
        },
      ]);

      const abortController = registerDownloadAbort(processId);

      try {
        const accumulated: File[] = [];
        let offset = 0;
        let total = 0;

        for (;;) {
          if (isDownloadCancelled(processId) || abortController.signal.aborted) {
            return;
          }

          const response = await fileService.getFiles(
            { ...baseFilters, offset, count: PAGE_SIZE },
            undefined,
            { signal: abortController.signal },
          );

          if (isDownloadCancelled(processId)) {
            return;
          }

          const page = response?.myFiles ?? [];
          total =
            typeof response?.total === 'number' ? response.total : accumulated.length + page.length;
          accumulated.push(...page);

          const progressPercentage =
            total > 0 ? Math.min(99, Math.round((accumulated.length / total) * 100)) : 100;

          setFileDownloadProcessStatuses((previousStatuses) =>
            previousStatuses.map((status) =>
              status.processId === processId
                ? {
                    ...status,
                    status: FileDownloadStatusEnum.Processing,
                    progressPercentage,
                  }
                : status,
            ),
          );

          // Stop on empty page or when total reached — never on short page alone
          // (API may return < PAGE_SIZE while total says more remain).
          if (page.length === 0 || accumulated.length >= total) {
            break;
          }
          // Advance by items received so short pages do not skip rows.
          offset += page.length;
        }

        if (isDownloadCancelled(processId)) {
          return;
        }

        if (accumulated.length === 0) {
          message.info(t('files.exportFolderFileList.empty'));
          setFileDownloadProcessStatuses((previousStatuses) =>
            previousStatuses.filter((status) => status.processId !== processId),
          );
          return;
        }

        const buildOptions = {
          columns,
          tagsJoin,
          dateFormatter: createExportFolderFileListDateFormatter(dateFormat, locale),
          headers: {
            id: t('files.exportFolderFileList.id'),
            name: t('common.fileName'),
            type: t('files.exportFolderFileList.extension'),
            size: t('common.fileSize'),
            lastModified: t('common.lastModified'),
            fileType: t('common.fileType'),
            isShared: t('files.exportFolderFileList.isShared'),
            createdBy: t('common.createdBy'),
            sharedBy: t('common.sharedBy'),
            tags: t('common.tags'),
          },
          folderLabel: t('common.folder'),
          yesLabel: t('common.yes'),
          noLabel: t('common.no'),
          tagTitleById,
        };

        let content: string;
        switch (format) {
          case 'json':
            content = buildExportFolderFileListJson(accumulated, buildOptions);
            break;
          case 'excelXml':
            content = buildExportFolderFileListSpreadsheetMl(accumulated, buildOptions);
            break;
          case 'csv':
          default:
            content =
              CSV_UTF8_BOM +
              buildExportFolderFileListCsv(accumulated, {
                ...buildOptions,
                delimiter,
              });
            break;
        }

        const blob = new Blob([content], { type: BLOB_MIME_BY_FORMAT[format] });
        triggerBlobDownload(blob, filename);

        setFileDownloadProcessStatuses((previousStatuses) =>
          previousStatuses.map((status) =>
            status.processId === processId
              ? {
                  ...status,
                  status: FileDownloadStatusEnum.Success,
                  progressPercentage: 100,
                  name: filename,
                }
              : status,
          ),
        );

        // ponytail: brief Success flash then drop — drawer has no auto-dismiss for direct downloads
        window.setTimeout(() => {
          setFileDownloadProcessStatuses((previousStatuses) =>
            previousStatuses.filter((status) => status.processId !== processId),
          );
        }, 2500);
      } catch (error) {
        if (isDownloadCancelled(processId)) {
          return;
        }
        const isAborted =
          error instanceof Error &&
          (error.name === 'AbortError' || (error as { aborted?: boolean }).aborted === true);
        if (isAborted || abortController.signal.aborted) {
          return;
        }
        notification.error({
          message: t('files.exportFolderFileList.failed'),
        });
        setFileDownloadProcessStatuses((previousStatuses) =>
          previousStatuses.map((status) =>
            status.processId === processId
              ? {
                  ...status,
                  status: FileDownloadStatusEnum.Failed,
                  progressPercentage: 0,
                  name: filename,
                }
              : status,
          ),
        );
      } finally {
        unregisterDownloadAbort(processId);
      }
    },
    [locale, message, notification, setFileDownloadProcessStatuses, t],
  );

  return {
    startExport,
    isExportRunning,
  };
};
